export const XER_CLASSES = [
  { id: 'every', label: 'Every XER' },
  { id: 'latest', label: 'Latest dated (not Current)' },
  { id: 'current', label: 'Current' },
];

export const DEFAULT_XER_CLASSES = ['current'];

export const WINDOW_HORIZONS = [
  { id: 'future', label: 'From today' },
  { id: 'all', label: 'All weeks' },
  { id: 'past', label: 'Through last week' },
];

export const DEFAULT_WINDOW_HORIZON = 'future';

function ymdOrNull(value) {
  const s = String(value || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}

export function localTodayYmd(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function sundayOnOrBefore(ymd) {
  const [y, m, d] = String(ymd).split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - dt.getUTCDay());
  return dt.toISOString().slice(0, 10);
}

function addDaysYmd(ymd, days) {
  const [y, m, d] = String(ymd).split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

export function resolveDateWindow(filters = {}, todayYmd = localTodayYmd()) {
  const horizon = WINDOW_HORIZONS.some((h) => h.id === filters.horizon) ? filters.horizon : DEFAULT_WINDOW_HORIZON;
  const today = ymdOrNull(todayYmd) || localTodayYmd();
  const thisSunday = sundayOnOrBefore(today);
  const lastSaturday = addDaysYmd(thisSunday, -1);
  let from = ymdOrNull(filters.from);
  let to = ymdOrNull(filters.to);
  if (horizon === 'future') {
    from = !from || from < thisSunday ? thisSunday : from;
  } else if (horizon === 'past') {
    to = !to || to >= thisSunday ? lastSaturday : to;
  }
  if (from && to && from > to) {
    if (horizon === 'past') from = null;
    else to = from;
  }
  return { horizon, from, to, thisSunday };
}

export function windowSummary(horizon, from, to) {
  const win = resolveDateWindow({ horizon, from, to });
  if (win.horizon === 'future' && !to) return 'From today';
  if (win.horizon === 'past' && !from) return 'Through last week';
  if (win.horizon === 'all' && !win.from && !win.to) return 'All weeks';
  if (win.from && win.to) return `${win.from} → ${win.to}`;
  if (win.from) return `From ${win.from}`;
  if (win.to) return `Through ${win.to}`;
  return WINDOW_HORIZONS.find((h) => h.id === win.horizon)?.label || 'From today';
}

export const LEGEND_OPTIONS = [
  { id: 'total', label: 'One colour' },
  { id: 'workbook', label: 'File name' },
  { id: 'project', label: 'Project ID' },
  { id: 'resource', label: 'Resource' },
  { id: 'resource_discipline', label: 'Discipline' },
  { id: 'shop', label: 'Shop' },
  { id: 'activity', label: 'Activity' },
  { id: 'rsrc_type', label: 'Resource type' },
  { id: 'wbs', label: 'WBS' },
  { id: 'calendar', label: 'P6 calendar' },
  { id: 'status', label: 'Active / Forecasted' },
];

export const DEFAULT_LEGEND_BY = 'workbook';

/** EVM-style P6 legend key: activity code type, UDF, or resource code (e.g. Resource Standardization). */
export function isP6LegendId(id) {
  return /^(actv|udf|rcat):\d+$/.test(String(id || '').trim());
}

/**
 * Legend catalog from the analytics payload — only the P6 fields that add a
 * dimension the portal keys do not already cover (built-in rsrc_id / task_code
 * duplicate Resource / Activity, so they are dropped).
 */
export function p6LegendFields(fields) {
  return (fields || []).filter((f) => f && isP6LegendId(f.id));
}

export function legendLabel(id, fields) {
  const portal = LEGEND_OPTIONS.find((o) => o.id === id)?.label;
  if (portal) return portal;
  const p6 = (fields || []).find((f) => f && f.id === id);
  return p6 ? p6.label : id;
}

/**
 * Radio groups for Colour by / Also split by: portal keys first, then one
 * group per P6 catalog group (Activity code, UDF (activity), Resource code, …)
 * — the same catalog the EVM report legend uses.
 */
export function legendGroups(fields, { omit = [] } = {}) {
  const skip = new Set(omit.filter(Boolean));
  const groups = [];
  const portal = LEGEND_OPTIONS.filter((o) => !skip.has(o.id));
  if (portal.length) groups.push({ label: fields && fields.length ? 'Portal' : '', items: portal });
  const p6 = p6LegendFields(fields).filter((f) => !skip.has(f.id));
  const byGroup = new Map();
  p6.forEach((f) => {
    const g = f.group || 'P6';
    if (!byGroup.has(g)) byGroup.set(g, []);
    byGroup.get(g).push({ id: f.id, label: f.label });
  });
  [...byGroup.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .forEach(([label, items]) => groups.push({ label, items }));
  return groups;
}

/** Programs still on the list after a portfolio is checked (children of those portfolios). */
export function programsVisibleForPortfolios(programs, portfolios, portfolioIds) {
  const selected = [...(portfolioIds || [])].map(String);
  if (!selected.length) return programs || [];
  const kids = new Set();
  (portfolios || []).forEach((p) => {
    if (!selected.includes(String(p.id))) return;
    (p.childProgramIds || p.child_program_ids || []).forEach((id) => {
      if (id != null) kids.add(String(id));
    });
  });
  if (!kids.size) return [];
  return (programs || []).filter((p) => kids.has(String(p.id)));
}

/**
 * Closed-label for Program / Portfolio. An empty selection is All (no filter).
 * Checking every named set is still a filter — jobs outside those sets drop out —
 * so a single checked program stays "Goodyear" even when it is the only one.
 */
export function namedSetSummary(selected, opts) {
  const options = opts || [];
  if (!options.length) return 'None';
  const chosen = options.filter((p) => selected && selected.has(String(p.id)));
  if (!chosen.length) return 'All';
  if (chosen.length === 1) return chosen[0].label || String(chosen[0].id);
  return `${chosen.length} selected`;
}

export const SLICER_CATALOG = [
  { id: 'xer', label: 'XER copy', group: 'P6', defaultOn: true, p6Only: true },
  { id: 'source', label: 'Source', group: 'Histogram', defaultOn: true },
  { id: 'legend', label: 'Legend', group: 'Histogram', defaultOn: true },
  { id: 'portfolio', label: 'Portfolio', group: 'Histogram', defaultOn: true },
  { id: 'program', label: 'Program', group: 'Histogram', defaultOn: true },
  { id: 'project', label: 'Project', group: 'Histogram', defaultOn: true },
  { id: 'discipline', label: 'Resource discipline', group: 'P6', defaultOn: true },
  { id: 'shop', label: 'Shop', group: 'Histogram', defaultOn: true },
  { id: 'status', label: 'Status', group: 'Histogram', defaultOn: false },
  { id: 'resource', label: 'Resource', group: 'P6', defaultOn: false },
  { id: 'activity', label: 'Activity', group: 'P6', defaultOn: false },
  { id: 'rsrcType', label: 'Resource type', group: 'P6', defaultOn: true },
  { id: 'wbs', label: 'WBS', group: 'P6', defaultOn: false },
  { id: 'calendar', label: 'P6 calendar', group: 'P6', defaultOn: false },
];

export const FAB_SLICER_PANELS_KEY = 'fab-histogram-slicer-panels-v1';

export function loadSlicerPanels() {
  const defaults = Object.fromEntries(SLICER_CATALOG.map((s) => [s.id, !!s.defaultOn]));
  try {
    const raw = localStorage.getItem(FAB_SLICER_PANELS_KEY);
    if (!raw) return defaults;
    return { ...defaults, ...JSON.parse(raw) };
  } catch {
    return defaults;
  }
}

/**
 * + Add slicer groups: the fixed portal / P6 lists, then every P6 column the
 * legend can colour by (activity code, UDF, resource code).
 */
export function slicerPickerGroups(legendFields, { p6Source = true } = {}) {
  const groups = [];
  const histogram = SLICER_CATALOG.filter((s) => s.group === 'Histogram');
  if (histogram.length) groups.push({ label: 'Histogram', items: histogram });
  const p6 = SLICER_CATALOG.filter((s) => s.group === 'P6' && (p6Source || !s.p6Only));
  if (p6.length) groups.push({ label: 'P6', items: p6 });
  if (p6Source) {
    legendGroups(p6LegendFields(legendFields))
      .filter((g) => g.label && g.label !== 'Portal')
      .forEach((g) => {
        groups.push({
          label: g.label,
          items: g.items.map((item) => ({ id: item.id, label: item.label, group: g.label })),
        });
      });
  }
  return groups;
}

export function saveSlicerPanels(panels) {
  try {
    localStorage.setItem(FAB_SLICER_PANELS_KEY, JSON.stringify(panels));
  } catch {
    /* ignore */
  }
}

export function defaultLaborTypes(options) {
  const list = (options || []).map((d) => String(d)).filter(Boolean);
  return list.filter((d) => d.trim().toLowerCase() === 'labor');
}

export function defaultResourceDisciplines(options) {
  const list = (options || []).map((d) => String(d)).filter(Boolean);
  const exact = list.filter((d) => d.trim().toLowerCase() === 'fabrication');
  if (exact.length) return exact;
  return list.filter((d) => /\bfab/i.test(d));
}

export function isFabricationDisciplineSet(disciplines) {
  const sel = [...(disciplines || [])];
  return sel.length > 0 && defaultResourceDisciplines(sel).length === sel.length;
}

export const PAGE_TITLE = 'Corporation Resource Histogram';

/** Page title is fixed; the discipline filter shows as a chip next to it. */
export function histogramTitle() {
  return PAGE_TITLE;
}

/** Short chip beside the title describing the discipline scope. */
export function histogramScopeChip(disciplines) {
  const sel = [...(disciplines || [])];
  if (!sel.length) return '';
  if (isFabricationDisciplineSet(sel)) return 'Fabrication only';
  if (sel.length === 1) return sel[0];
  return `${sel.length} disciplines`;
}
